/**
 * Toast component for SnapHub PWA.
 *
 * Shows success (green) and error (red) toast messages with auto-dismiss.
 * Toasts are positioned fixed at the top-right of the viewport and stack vertically.
 *
 * Validates: Requirements 43.3
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToastType = 'success' | 'error';

interface ToastEntry {
  element: HTMLElement;
  timerId: ReturnType<typeof setTimeout>;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let toastContainer: HTMLElement | null = null;
const activeToasts: ToastEntry[] = [];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_DURATION = 3000;

// ---------------------------------------------------------------------------
// SVG Icons
// ---------------------------------------------------------------------------

const successIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`;

const errorIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function ensureContainer(): HTMLElement {
  if (toastContainer && document.body.contains(toastContainer)) {
    return toastContainer;
  }

  toastContainer = document.createElement('div');
  toastContainer.className = 'toast-container';
  toastContainer.setAttribute('aria-live', 'polite');
  toastContainer.setAttribute('aria-atomic', 'false');
  document.body.appendChild(toastContainer);
  return toastContainer;
}

function removeToast(entry: ToastEntry): void {
  clearTimeout(entry.timerId);
  entry.element.classList.add('toast--exit');

  const onEnd = () => {
    entry.element.remove();
    const idx = activeToasts.indexOf(entry);
    if (idx !== -1) {
      activeToasts.splice(idx, 1);
    }
    // Remove container if empty
    if (toastContainer && activeToasts.length === 0) {
      toastContainer.remove();
      toastContainer = null;
    }
  };

  entry.element.addEventListener('animationend', onEnd, { once: true });

  // Fallback: remove after 300ms if animationend doesn't fire (e.g. in tests)
  setTimeout(onEnd, 300);
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Show a toast notification.
 *
 * @param message - The message to display
 * @param type - 'success' or 'error'
 * @param duration - Auto-dismiss duration in ms (default: 3000)
 */
export function showToast(
  message: string,
  type: ToastType = 'success',
  duration: number = DEFAULT_DURATION,
): HTMLElement {
  const container = ensureContainer();

  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;
  toast.setAttribute('role', 'status');
  toast.innerHTML = `
    <span class="toast__icon">${type === 'success' ? successIcon : errorIcon}</span>
    <span class="toast__message">${escapeHtml(message)}</span>
    <button class="toast__close" aria-label="Schließen" type="button">&times;</button>
  `;

  // Close button handler
  const closeBtn = toast.querySelector('.toast__close') as HTMLButtonElement;
  const entry: ToastEntry = {
    element: toast,
    timerId: setTimeout(() => removeToast(entry), duration),
  };

  closeBtn.addEventListener('click', () => removeToast(entry));

  activeToasts.push(entry);
  container.appendChild(toast);

  return toast;
}

/**
 * Remove all active toasts immediately.
 */
export function clearAllToasts(): void {
  // Copy array since removeToast mutates it
  [...activeToasts].forEach((entry) => {
    clearTimeout(entry.timerId);
    entry.element.remove();
  });
  activeToasts.length = 0;

  if (toastContainer) {
    toastContainer.remove();
    toastContainer = null;
  }
}

/**
 * Get the number of currently active toasts.
 */
export function getActiveToastCount(): number {
  return activeToasts.length;
}
